import Point from "@arcgis/core/geometry/Point";
import Graphic from "@arcgis/core/Graphic";
import FeatureLayer from "@arcgis/core/layers/FeatureLayer";
import { Edits } from "@arcgis/core/editing/types";
import GraphicsLayer from "@arcgis/core/layers/GraphicsLayer";
import UniqueValueRenderer from "@arcgis/core/renderers/UniqueValueRenderer";
import RiverAccessIcon from '../images/RiverAccess.png';
import RiverAccessPrimitiveIcon from '../images/RiverAccessPrimitive.png';
import PointOfInterestIcon from '../images/PointOfInterest.svg';
import PointOfInterestStreamIcon from '../images/PointOfInterestStream.svg';
import PointOfInterestBluffIcon from '../images/PointOfInterestBluff.svg';
import PointOfInterestHollowIcon from '../images/PointOfInterestHollow.svg';
import PointOfInterestTrailIcon from '../images/PointOfInterestTrail.svg';
import PointOfInterestBridgeIcon from '../images/PointOfInterestBridge.svg';
import PointSymbol3D from "@arcgis/core/symbols/PointSymbol3D";
import SimpleLineSymbol from "@arcgis/core/symbols/SimpleLineSymbol";
import SimpleRenderer from "@arcgis/core/renderers/SimpleRenderer";
import buffaloRiverPath from "../data/buffaloRiverPath.json"
import Polyline from "@arcgis/core/geometry/Polyline";
import SceneView from "@arcgis/core/views/SceneView";
import FeatureLayerView from "@arcgis/core/views/layers/FeatureLayerView";
import { ResourceHandle } from "@arcgis/core/core/Handles";
import Map from "@arcgis/core/Map"
import { accessPoints } from "../data/accessPoints";
import { riverPointsOfInterest, PoiType } from "../data/riverPointsOfInterest";

export let map: Map | null = null
export const setMap = (newMap: Map) => {
    map = newMap
}

export let view: SceneView | null = null
export const setView = (newView: SceneView) => {
    view = newView
}

const accessPointColor = "#0079c1"
// Orange reads as a caution next to the standard blue, and the two stay distinct
// for the common forms of color blindness. The primitive icon is the standard
// one recolored to this, so the two need to be kept in step.
export const primitiveAccessPointColor = "#e65100"

const accessPointGraphics = accessPoints.map((accessPoint, index) => (new Graphic({
    geometry: new Point(accessPoint.coord),
    attributes: {
        ObjectID: index,
        Name: accessPoint.name,
        Type: accessPoint.isPrimitive ? "PrimitiveAccess" : "Access"
    }
})))

type VerticalOffset = {
    screenLength: number
    maxWorldLength: number
    minWorldLength: number
}

const pointOfInterestVerticalOffset: VerticalOffset = {
    screenLength: 40,
    maxWorldLength: 1000,
    minWorldLength: 35,
}

// Access points sit well above points of interest, so where the two are close
// together the access point icon and its label clear the point of interest's
// rather than being drawn over them.
const accessPointVerticalOffset: VerticalOffset = {
    screenLength: 100,
    maxWorldLength: 2500,
    minWorldLength: 90,
}

const pointsRenderer = new UniqueValueRenderer({
    field: "Type",
    uniqueValueInfos: [
        {
            value: "Access",
            symbol: getPointsUniqueValueSymbol(
                RiverAccessIcon,
                accessPointColor,
                accessPointVerticalOffset
            )
        },
        {
            value: "PrimitiveAccess",
            symbol: getPointsUniqueValueSymbol(
                RiverAccessPrimitiveIcon,
                primitiveAccessPointColor,
                accessPointVerticalOffset
            )
        }
    ],
})

function getPointsUniqueValueSymbol(name: string, color: string, verticalOffset: VerticalOffset) {
    return new PointSymbol3D({
        symbolLayers: [
            {
            type: "icon",
            resource: {
                href: name,
            },
            size: 20,
            outline: {
                color: "white",
                size: 2,
            },
            occludedVisibility: {
                mode: "adaptive",
            },
            },
        ],
        verticalOffset,
        callout: {
            type: "line", 
            color: "white",
            size: 2,
            border: {
            color: color,
            },
        },
    })
}

export const pointsLayer = new FeatureLayer({
  source: accessPointGraphics,
  renderer: pointsRenderer,
  labelingInfo: [{
      symbol: {
          type: "text",
          color: "white",
          haloColor: accessPointColor,
          haloSize: 1,
          font: {
              family: "sans-serif",
              size: 12,
          },
      },
      labelPlacement: "above-center",
      labelExpressionInfo: {
          expression: "$feature.Name",
      },
      maxScale: 0,
      minScale: 25000000,
  }],
  fields: [{
      name: "ObjectID",
      alias: "ObjectID",
      type: "oid"
  },
  {
      name: "Name",
      alias: "Name",
      type: "string"
  },
  {
      name: "Type",
      alias: "Type",
      type: "string"
  }],
  objectIdField: "ObjectID",
})

export const pointOfInterestColor = "#8e24aa"

// Names repeat along the river (two Bee Bluffs, two Beech Creeks), so points of
// interest are identified by their position in the source data. That index is
// carried in its own Index field rather than read back off ObjectID, which the
// layer owns and is free to reassign. Descriptions stay in the source data too,
// since several of them are longer than a string field holds.
const pointOfInterestGraphics = riverPointsOfInterest.map((pointOfInterest, index) => (new Graphic({
    geometry: new Point(pointOfInterest.coord),
    attributes: {
        ObjectID: index + 1,
        Index: index,
        Name: pointOfInterest.name,
        Type: pointOfInterest.type
    }
})))

export const getPointOfInterest = (index: number) => riverPointsOfInterest[index]

// Each type gets its own glyph so the map can be read without clicking. They
// share one color on purpose, which keeps them legible as a single family and
// lets the labels below keep a single halo color. Typing the record by PoiType
// means adding a type to the source data fails the build until it has a glyph.
export const pointOfInterestIconsByType: Record<PoiType, string> = {
    stream: PointOfInterestStreamIcon,
    bluff: PointOfInterestBluffIcon,
    hollow: PointOfInterestHollowIcon,
    trail: PointOfInterestTrailIcon,
    bridge: PointOfInterestBridgeIcon,
    other: PointOfInterestIcon,
}

export const pointOfInterestTypeLabels: Record<PoiType, string> = {
    stream: "Streams",
    bluff: "Bluffs",
    hollow: "Hollows",
    trail: "Trails",
    bridge: "Bridges",
    other: "Other",
}

export const pointOfInterestTypes = Object.keys(pointOfInterestIconsByType) as PoiType[]

const pointsOfInterestRenderer = new UniqueValueRenderer({
    field: "Type",
    uniqueValueInfos: Object.entries(pointOfInterestIconsByType).map(([type, icon]) => ({
        value: type,
        symbol: getPointsUniqueValueSymbol(icon, pointOfInterestColor, pointOfInterestVerticalOffset)
    })),
    // Anything the map above misses still draws, rather than dropping off the map.
    defaultSymbol: getPointsUniqueValueSymbol(PointOfInterestIcon, pointOfInterestColor, pointOfInterestVerticalOffset),
})

export const pointsOfInterestLayer = new FeatureLayer({
  source: pointOfInterestGraphics,
  renderer: pointsOfInterestRenderer,
  labelingInfo: [{
      symbol: {
          type: "text",
          color: "white",
          haloColor: pointOfInterestColor,
          haloSize: 1,
          font: {
              family: "sans-serif",
              size: 12,
          },
      },
      labelPlacement: "above-center",
      labelExpressionInfo: {
          expression: "$feature.Name",
      },
      maxScale: 0,
      minScale: 25000000,
  }],
  fields: [{
      name: "ObjectID",
      alias: "ObjectID",
      type: "oid"
  },
  {
      name: "Index",
      alias: "Index",
      type: "integer"
  },
  {
      name: "Name",
      alias: "Name",
      type: "string"
  },
  {
      name: "Type",
      alias: "Type",
      type: "string"
  }],
  objectIdField: "ObjectID",
  // Nothing in the renderer or labels reads Index, so it is requested explicitly
  // to keep it on the graphics that hit tests hand back.
  outFields: ["*"],
})

// The layer stays on the map and is filtered rather than added and removed.
// An IN list cannot be empty, so hiding every type hides the layer instead, and
// showing every type drops the filter so nothing the renderer's default symbol
// catches gets left off.
export const setVisiblePointOfInterestTypes = (types: PoiType[]) => {
    pointsOfInterestLayer.visible = types.length > 0
    pointsOfInterestLayer.definitionExpression = types.length === 0 || types.length === pointOfInterestTypes.length
        ? ""
        : `Type IN (${types.map((type) => `'${type}'`).join(", ")})`
}

export const selectionLayer = new GraphicsLayer()

const selectedSegmentOutlineSymbol = new SimpleLineSymbol({
    color: [0, 75, 75, 0.75],
    width: 12.5,
    cap: "round",
})

const selectedSegmentDashSymbol = new SimpleLineSymbol({
    color: [0, 255, 255, 0.75],
    width: 12.5,
    cap: "round",
    style: "short-dash",
})

export const setSelectedRiverSegment = (segment: Polyline | null) => {
    selectionLayer.removeAll()
    if (segment === null) return

    selectionLayer.addMany([
        new Graphic({ geometry: segment, symbol: selectedSegmentOutlineSymbol }),
        new Graphic({ geometry: segment, symbol: selectedSegmentDashSymbol }),
    ])
}

let accessPointHighlightHandle: ResourceHandle | null = null
export const setHighlightedAccessPoints = async (names: string[]) => {
    accessPointHighlightHandle?.remove()
    accessPointHighlightHandle = null

    if (view === null || names.length === 0) return

    const graphicsToHighlight = accessPointGraphics.filter((graphic) => names.includes(graphic.attributes.Name))
    if (graphicsToHighlight.length === 0) return

    const layerView = await view.whenLayerView(pointsLayer) as FeatureLayerView
    accessPointHighlightHandle = layerView.highlight(graphicsToHighlight)
}

let pointOfInterestHighlightHandle: ResourceHandle | null = null
export const setHighlightedPointOfInterest = async (index: number | null) => {
    pointOfInterestHighlightHandle?.remove()
    pointOfInterestHighlightHandle = null

    if (view === null || index === null) return

    const graphicToHighlight = pointOfInterestGraphics.find((graphic) => graphic.attributes.Index === index)
    if (graphicToHighlight === undefined) return

    const layerView = await view.whenLayerView(pointsOfInterestLayer) as FeatureLayerView
    pointOfInterestHighlightHandle = layerView.highlight(graphicToHighlight)
}

function getRiverGraphics(polylineFloatability: number[]) {
    if (polylineFloatability.length !== buffaloRiverPath.length-1)
        throw new Error("mismatch between floatability scores and river segments")

    const graphics: Graphic[] = []

    for(let i=0; i<buffaloRiverPath.length-2; i++) {
        graphics.push(new Graphic({
            geometry: new Polyline({
                paths: [[buffaloRiverPath[i], buffaloRiverPath[i+1]]]
            }),
            attributes: {
                ObjectID: i,
                Floatability: polylineFloatability[i]
            }
        }))
    }

    return graphics
}

export const floatabilityColors = {
    veryLow: "#5813fcff",
    low: "#1cc2fdff",
    moderate: "#7dfd94ff",
    high: "#f5c926ff",
    flood: "#ff2b18ff"
}

const floatabilityCategoryStops = [
    {
        value: 0,
        label: "Very Low",
        color: floatabilityColors.veryLow,
        guidance: "Too low for most floating. Expect to drag your boat across gravel bars and shallow, rocky stretches."
    },
    {
        value: 250,
        label: "Low",
        color: floatabilityColors.low,
        guidance: "Floatable, but low. Expect some scraping and a few spots where you'll need to hop out and walk your boat."
    },
    {
        value: 500,
        label: "Moderate",
        color: floatabilityColors.moderate,
        guidance: "Ample water for floating. Good conditions for most paddlers and a comfortable, relaxed trip."
    },
    {
        value: 750,
        label: "High",
        color: floatabilityColors.high,
        guidance: "Fast current. Experienced floaters only."
    },
    {
        value: 1000,
        label: "Flood",
        color: floatabilityColors.flood,
        guidance: "Flood stage. The river is dangerous and should be considered closed to floating."
    },
]

export const getFloatabilityCategory = (score: number) => {
    return floatabilityCategoryStops.reduce((closest, stop) =>
        Math.abs(stop.value - score) < Math.abs(closest.value - score) ? stop : closest
    )
}

const riverRenderer = new SimpleRenderer({
    symbol: new SimpleLineSymbol({
        color: "#ff00f7",
        width: 5,
    }),
    visualVariables: [
      {
        type: "color",
        field: "Floatability",
        stops: [
          { value: 0, color: floatabilityColors.veryLow },
          { value: 250, color: floatabilityColors.low },
          { value: 500, color: floatabilityColors.moderate },
          { value: 750, color: floatabilityColors.high },
          { value: 1000, color: floatabilityColors.flood },
        ],
      },
    ],
})
export let riverLayer: FeatureLayer | null = null
export const createRiverLayer = (polylineFloatability: number[]) => {
    riverLayer = new FeatureLayer({
        source: getRiverGraphics(polylineFloatability),
        renderer: riverRenderer,
        fields: [{
            name: "ObjectID",
            alias: "ObjectID",
            type: "oid"
        },
        {
            name: "Floatability",
            alias: "Floatability",
            type: "double"
        }],
        objectIdField: "ObjectID",
    })
    return riverLayer
}
export const applyRiverFloatability = async (polylineFloatability: number[]) => {
    if (riverLayer === null)
        return
    const edits: Edits = {
        updateFeatures: riverLayer.source.map((graphic, i) => {
            graphic.setAttribute("Floatability", polylineFloatability[i])
            return graphic
        })
    }
    await riverLayer.applyEdits(edits)
}